import type {
  GarmentAnalysisJobResponse,
  GarmentAnalysisResponse,
  GarmentSuggestion,
  NormalizedCrop,
} from '../../shared/smartDetector';
import { readError } from './api';

const MAX_ANALYSIS_EDGE = 2048;
const ANALYSIS_JPEG_QUALITY = 0.9;
const DIRECT_AI_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export async function prepareSmartDetectorPhoto(photo: File): Promise<File> {
  if (typeof createImageBitmap !== 'function') {
    if (DIRECT_AI_TYPES.has(photo.type.toLowerCase())) return photo;
    throw new Error(
      'This device could not prepare the HEIC photo for analysis. Choose a JPEG, PNG, or WebP copy.',
    );
  }

  try {
    const bitmap = await createImageBitmap(photo, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_ANALYSIS_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      throw new Error('Canvas is unavailable.');
    }
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', ANALYSIS_JPEG_QUALITY),
    );
    if (!blob) throw new Error('JPEG conversion failed.');
    return new File([blob], 'smart-detector-photo.jpg', {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    if (DIRECT_AI_TYPES.has(photo.type.toLowerCase())) return photo;
    throw new Error(
      'This device could not prepare the HEIC photo for analysis. Choose a JPEG, PNG, or WebP copy.',
    );
  }
}

export async function analyzeGarmentPhoto(
  photo: File,
  analysisKey: string,
  onStatus?: (message: string) => void,
): Promise<GarmentSuggestion[]> {
  const form = new FormData();
  onStatus?.('Preparing the photo for garment detection…');
  form.set('photo', await prepareSmartDetectorPhoto(photo));
  onStatus?.('Checking every visible garment…');

  let response: Response;
  try {
    response = await fetch('/api/ai/analyze', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-Analysis-Key': analysisKey,
      },
      body: form,
    });
  } catch {
    onStatus?.('Connection interrupted. Recovering the cloud analysis…');
    return recoverGarmentAnalysis(analysisKey, onStatus);
  }

  if (response.status === 409) {
    onStatus?.('The cloud is still reading the photo…');
    return recoverGarmentAnalysis(analysisKey, onStatus);
  }
  if (!response.ok) throw new Error(await readError(response));
  return ((await response.json()) as GarmentAnalysisResponse).suggestions;
}

export async function recoverGarmentAnalysis(
  analysisKey: string,
  onStatus?: (message: string) => void,
): Promise<GarmentSuggestion[]> {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    const recovered = await getGarmentAnalysisJob(analysisKey);
    if (recovered?.status === 'completed') {
      if (recovered.suggestions.length > 0) return recovered.suggestions;
      throw new Error(
        'No garments were detected after two visual checks. Try a closer crop, or use regular photo upload.',
      );
    }
    if (recovered?.status === 'failed') throw new Error(recovered.error);
    onStatus?.(`Recovering analysis… ${Math.min(95, 15 + attempt * 3)}%`);
    await new Promise((resolve) => setTimeout(resolve, 2_000));
  }
  throw new Error(
    'Smart Detector is taking longer than expected. Keep this photo selected and try again.',
  );
}

export async function getGarmentAnalysisJob(
  jobId: string,
): Promise<GarmentAnalysisJobResponse | null> {
  try {
    const response = await fetch(`/api/ai/jobs/${encodeURIComponent(jobId)}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(await readError(response));
    return (await response.json()) as GarmentAnalysisJobResponse;
  } catch {
    return null;
  }
}

export function normalizeCrop(crop: NormalizedCrop): NormalizedCrop {
  const x = Math.min(0.95, Math.max(0, crop.x));
  const y = Math.min(0.95, Math.max(0, crop.y));
  return {
    x,
    y,
    width: Math.min(1 - x, Math.max(0.05, crop.width)),
    height: Math.min(1 - y, Math.max(0.05, crop.height)),
  };
}

export async function cropGarmentPhoto(
  photo: File,
  crop: NormalizedCrop,
  index: number,
): Promise<File> {
  if (typeof createImageBitmap !== 'function') return photo;

  try {
    const bitmap = await createImageBitmap(photo);
    const bounded = normalizeCrop(crop);
    const sourceX = Math.round(bounded.x * bitmap.width);
    const sourceY = Math.round(bounded.y * bitmap.height);
    const sourceWidth = Math.max(1, Math.round(bounded.width * bitmap.width));
    const sourceHeight = Math.max(1, Math.round(bounded.height * bitmap.height));
    const scale = Math.min(1, 1600 / Math.max(sourceWidth, sourceHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sourceWidth * scale));
    canvas.height = Math.max(1, Math.round(sourceHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) return photo;

    context.drawImage(
      bitmap,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.9),
    );
    if (!blob) return photo;

    return new File([blob], `smart-crop-${index + 1}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    return photo;
  }
}
